import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Tabs, Tab, FormControl, InputLabel,
  Select, MenuItem, TextField, Paper, Button, Tooltip, Alert
} from '@mui/material';
import AiUsageMetricCards from '../../components/AiUsageMetricCards';
import AiUsageTimeSeries from '../../components/AiUsageTimeSeries';
import AiUsageBreakdowns from '../../components/AiUsageBreakdowns';
import AiUsageUserTable from '../../components/AiUsageUserTable';
import AiUsagePricingTab from '../../components/AiUsagePricingTab';
import { aiUsageApi } from '../../services/aiUsageApi';

export default function AiUsageDashboard() {
  const [tab, setTab]           = useState(0);
  const [from, setFrom]         = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  const [to, setTo]             = useState(new Date());
  const [userId, setUserId]     = useState('');
  const [app, setApp]           = useState('');
  const [useCase, setUseCase]   = useState('');
  const [model, setModel]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [summary, setSummary]   = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [byApp, setByApp]       = useState([]);
  const [byUseCase, setByUseCase] = useState([]);
  const [byUser, setByUser]     = useState([]);
  const [filterOptions, setFilterOptions] = useState({ use_cases: [], models: [] });
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshRate, setRefreshRate] = useState(30);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const autoRefreshRef = useRef(null);

  const load = useCallback(async () => {
    const filters = {
      from: from?.toISOString(),
      to:   to?.toISOString(),
      ...(userId  ? { userId }  : {}),
      ...(app     ? { app }     : {}),
      ...(useCase ? { useCase } : {}),
      ...(model   ? { model }   : {}),
    };
    setLoading(true);
    try {
      const [s, ts, ba, buc, bu] = await Promise.all([
        aiUsageApi.getSummary(filters),
        aiUsageApi.getTimeseries({ ...filters, bucket: 'day' }),
        aiUsageApi.getBreakdownByApp(filters),
        aiUsageApi.getBreakdownByUseCase(filters),
        aiUsageApi.getBreakdownByUser(filters),
      ]);
      // 999.5111: guard against malformed API responses (200 with wrong shape).
      // The API contract says getSummary returns { summary: {...} } and the
      // breakdown endpoints return { rows: [...] }. If the response is empty
      // or missing those keys, default to safe empty values so the dashboard
      // degrades to an empty-state instead of crashing.
      setSummary(s?.summary ?? null);
      setTimeseries(ts?.rows ?? []);
      setByApp(ba?.rows ?? []);
      setByUseCase(buc?.rows ?? []);
      setByUser(bu?.rows ?? []);
      setLastRefresh(new Date());
      setLoadError(null);
    } catch (e) {
      console.error('AI usage load error', e);
      // 999.1594 — this used to fail silently: charts stayed blank/stale with
      // no indication the fetch had actually failed vs. there being no data.
      setLoadError(e?.message || 'Failed to load AI usage data.');
    } finally {
      setLoading(false);
    }
  }, [from, to, userId, app, useCase, model]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    aiUsageApi.getFilterOptions().then(d => setFilterOptions(d)).catch(() => {});
  }, []);

  useEffect(() => {
    if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    if (autoRefresh) autoRefreshRef.current = setInterval(load, refreshRate * 1000);
    return () => { if (autoRefreshRef.current) clearInterval(autoRefreshRef.current); };
  }, [autoRefresh, refreshRate, load]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="h4">AI Usage</Typography>
        <Button size="small" variant="outlined" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : '↻ Refresh'}
        </Button>
        <FormControl size="small" sx={{ minWidth: 80 }}>
          <Select
            value={refreshRate}
            onChange={e => setRefreshRate(e.target.value)}
          >
            {[5, 10, 15, 20, 25, 30].map(s => (
              <MenuItem key={s} value={s}>{s}s</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Tooltip title={autoRefresh ? `Auto-refreshing every ${refreshRate}s` : `Auto-refresh every ${refreshRate}s`}>
          <Button
            size="small"
            variant={autoRefresh ? 'contained' : 'outlined'}
            onClick={() => setAutoRefresh(v => !v)}
          >
            {autoRefresh ? '⏸ Auto' : '▶ Auto'}
          </Button>
        </Tooltip>
        {lastRefresh && (
          <Typography variant="caption" color="text.secondary">
            Last: {lastRefresh.toLocaleTimeString()}
          </Typography>
        )}
      </Box>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLoadError(null)}>
          {loadError}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Usage" />
        <Tab label="Pricing Rates" />
      </Tabs>

      {tab === 0 && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <TextField
                label="From"
                type="datetime-local"
                size="small"
                value={from ? from.toISOString().slice(0,16) : ''}
                onChange={e => setFrom(e.target.value ? new Date(e.target.value) : null)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: '1 1 210px', minWidth: 210 }}
              />
              <TextField
                label="To"
                type="datetime-local"
                size="small"
                value={to ? to.toISOString().slice(0,16) : ''}
                onChange={e => setTo(e.target.value ? new Date(e.target.value) : null)}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: '1 1 210px', minWidth: 210 }}
              />
              <FormControl size="small" sx={{ flex: '1 1 140px', minWidth: 140 }}>
                <InputLabel>App</InputLabel>
                <Select value={app} label="App" onChange={e => setApp(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="resume-optimizer">resume-optimizer</MenuItem>
                  <MenuItem value="juggler">juggler</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: '1 1 160px', minWidth: 160 }}>
                <InputLabel>Use Case</InputLabel>
                <Select value={useCase} label="Use Case" onChange={e => setUseCase(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {(filterOptions.use_cases || []).map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: '1 1 160px', minWidth: 160 }}>
                <InputLabel>Model</InputLabel>
                <Select value={model} label="Model" onChange={e => setModel(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {(filterOptions.models || []).map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField
                label="User ID"
                size="small"
                value={userId}
                onChange={e => setUserId(e.target.value)}
                sx={{ flex: '1 1 160px', minWidth: 160 }}
              />
              <Button
                size="small"
                variant="outlined"
                sx={{ flexShrink: 0, alignSelf: 'center' }}
                onClick={() => {
                  setFrom(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
                  setTo(new Date());
                  setApp('');
                  setModel('');
                  setUseCase('');
                  setUserId('');
                }}
              >
                Clear
              </Button>
            </Box>
          </Paper>

          <AiUsageMetricCards summary={summary} loading={loading} />
          <AiUsageTimeSeries rows={timeseries} loading={loading} />
          <AiUsageBreakdowns byApp={byApp} byUseCase={byUseCase} loading={loading} />
          <AiUsageUserTable rows={byUser} loading={loading} onUserClick={setUserId} />
        </>
      )}

      {tab === 1 && <AiUsagePricingTab />}
    </Box>
  );
}
